/**
 * Hook for Whisper API voice recording and transcription (Windows Desktop)
 */
import { useState, useRef, useCallback } from 'react';
import { config } from '../config';

interface UseWhisperRecordingResult {
  isRecording: boolean;
  isTranscribing: boolean;
  isWarming: boolean;
  error: string | null;
  audioLevel: number;
  waveformData: number[];
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string | null>;
}

export function useWhisperRecording(): UseWhisperRecordingResult {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isWarming, setIsWarming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [waveformData, setWaveformData] = useState<number[]>(Array(64).fill(0));

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setIsWarming(true);

      // Request microphone access with optimal settings for speech recognition
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
          volume: 1.0
        } as any
      });

      // Create MediaRecorder with optimal settings for speech
      let options;
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        options = {
          mimeType: 'audio/webm;codecs=opus',
          audioBitsPerSecond: 64000
        };
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        options = {
          mimeType: 'audio/ogg;codecs=opus',
          audioBitsPerSecond: 64000
        };
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        options = {
          mimeType: 'audio/mp4',
          audioBitsPerSecond: 64000
        };
      } else {
        options = {
          audioBitsPerSecond: 64000
        };
      }

      const mediaRecorder = new MediaRecorder(stream, options);

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(250);
      mediaRecorderRef.current = mediaRecorder;
      streamRef.current = stream;

      // Set up audio visualization
      setupAudioVisualization(stream);

      // Show warming indicator briefly
      await new Promise(resolve => setTimeout(resolve, 500));

      setIsWarming(false);
      setIsRecording(true);
    } catch (err: any) {
      setIsWarming(false);
      const errorMsg = err.name === 'NotAllowedError'
        ? 'Microphone access denied. Please allow microphone access.'
        : 'Failed to start recording: ' + err.message;

      setError(errorMsg);
      console.error('[Whisper] Recording error:', err);
    }
  }, []);

  const stopRecording = useCallback(async (): Promise<string | null> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;

      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        resolve(null);
        return;
      }

      mediaRecorder.onstop = async () => {
        setIsRecording(false);

        stopAudioVisualization();

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        } else {
          mediaRecorder.stream.getTracks().forEach(track => track.stop());
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        audioChunksRef.current = [];

        setIsTranscribing(true);
        try {
          const transcription = await transcribeAudio(audioBlob);
          setIsTranscribing(false);
          resolve(transcription);
        } catch (err: any) {
          setError('Transcription failed: ' + err.message);
          setIsTranscribing(false);
          resolve(null);
        }
      };

      mediaRecorder.stop();
    });
  }, []);

  const setupAudioVisualization = useCallback((stream: MediaStream) => {
    try {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.6;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const updateAudioLevel = () => {
        if (!analyserRef.current) return;

        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyserRef.current.getByteFrequencyData(dataArray);

        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        const level = Math.min(100, Math.max(0, (average / 255) * 100));
        setAudioLevel(Math.round(level));

        const waveform: number[] = [];
        const step = Math.floor(bufferLength / 64);
        for (let i = 0; i < 64; i++) {
          const index = i * step;
          waveform.push(Math.min(100, (dataArray[index] / 255) * 100));
        }
        setWaveformData(waveform);

        animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
      };

      updateAudioLevel();
    } catch (err) {
      console.error('[Whisper] Failed to setup audio visualization:', err);
    }
  }, []);

  const stopAudioVisualization = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
    setWaveformData(Array(64).fill(0));
  }, []);

  return {
    isRecording,
    isTranscribing,
    isWarming,
    error,
    audioLevel,
    waveformData,
    startRecording,
    stopRecording,
  };
}

async function transcribeAudio(audioBlob: Blob): Promise<string> {
  const token = localStorage.getItem('access_token');

  if (!token) {
    throw new Error('Not authenticated');
  }

  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  formData.append('language', 'en');

  const response = await fetch(`${config.apiBaseUrl}/api/voice/transcribe`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Transcription failed' }));
    throw new Error(error.detail || 'Transcription failed');
  }

  const data = await response.json();
  return data.text || data.transcription || '';
}
